// src/components/Modules/Vehicles/AddVehicleForm.js

import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Modal, Button, Form, Spin, notification, Switch, Alert } from "antd";
import api from "../../../axiosConfig";
import dayjs from "../../../utils/dayjs"; // Usar dayjs configurado con timezone
import { useUser } from "../../../UserContext";

// Importar los componentes de sección
import VehicleInfo from "./VehicleInfo";
import OwnerInfo from "./OwnerInfo";
import AdminInfo from "./AdminInfo";
import ContractInfo from "./ContractInfo";
import ExpirationDatesInfo from "./ExpirationDatesInfo";
import GeneracionDocumentos from "../../common/GeneracionDocumentos";
import SiNoSinDefinir from "../../common/SiNoSinDefinir";

// Definir valores predeterminados
const defaultVehicle = {
  codigo: "",
  placa: "",
  numero_tarjeta_operacion: "",
  marca: "",
  modelo: "",
  linea: "",
  tipo: "",
  color: "",
  estado: "Activo",
  genera_fuec: true,
  genera_preoperacional: true,
  flota_propia: null, // null = sin definir
  capacidad_pasajeros: null,
  kilometraje: null,
  numero_motor: "",
  numero_chasis: "",
  propietario_numero_documento: "",
  propietario_tipo_documento_id: "",
  propietario_nombre: "",
  propietario_telefono: "",
  propietario_correo: "",
  propietario_direccion: "",
  propietario_barrio: "",
  propietario_localidad: "",
  propietario_ciudad: "",
  propietario_departamento: "",
  administrador_numero_documento: "",
  administrador_tipo_documento_id: "",
  administrador_nombre: "",
  administrador_telefono: "",
  administrador_correo: "",
  administrador_direccion: "",
  administrador_barrio: "",
  administrador_localidad: "",
  administrador_ciudad: "",
  administrador_departamento: "",
  administrador_acceso_app: false,
  zona_id: "",
  tipo_vinculacion: "",
  fecha_vinculacion: "",
  fecha_matricula: "",
  combustible: "",
  cilindraje: null,
  gps: "",
  empresa_vinculadora: "",
  nit: "",
  fecha_vencimiento_convenio: "",
  fecha_vencimiento_preventiva: "",
  empresa_preventiva_id: "",
  fecha_vencimiento_soat: "",
  empresa_soat_id: "",
  fecha_vencimiento_tecnomecanica: "",
  empresa_tecnomecanica_id: "",
  fecha_vencimiento_contractual: "",
  empresa_contractual_id: "",
  fecha_vencimiento_extracontractual: "",
  empresa_extracontractual_id: "",
  fecha_vencimiento_tarjeta_operacion: "",
  empresa_tarjeta_operacion_id: "",
  fecha_vencimiento_todo_riesgo: "",
  empresa_todo_riesgo_id: "",
};

const AddVehicleForm = ({
  open,
  handleClose,
  fetchVehicles,
  initialData = null, // Datos iniciales para edición
  isEdit = false, // Modo edición
}) => {
  const { user } = useUser();
  const [vehicle, setVehicle] = useState(defaultVehicle);
  const [errors, setErrors] = useState({});
  const [tiposDocumento, setTiposDocumento] = useState([]);
  const [aseguradoras, setAseguradoras] = useState([]);
  const [zonas, setZonas] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [ciudadesPropietario, setCiudadesPropietario] = useState([]);
  const [ciudadesAdministrador, setCiudadesAdministrador] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mismoAdministrador, setMismoAdministrador] = useState(false);
  // Cuenta existente con el documento del administrador (ingreso a las apps de conductor)
  const [cuentaAdministrador, setCuentaAdministrador] = useState(null);
  // Estado para tracking de cambio de estado
  const [initialEstado, setInitialEstado] = useState(null);
  const [estadoObservacion, setEstadoObservacion] = useState("");

  // Funciones para obtener los datos necesarios
  const fetchTiposDocumento = useCallback(async () => {
    try {
      const response = await api.get("/tipos-documento");
      setTiposDocumento(response.data);
    } catch (error) {
      console.error("Error al obtener tipos de documento:", error);
      notification.error({
        message: "Error",
        description: "No se pudieron cargar los tipos de documento.",
      });
    }
  }, []);

  const fetchAseguradoras = useCallback(async () => {
    try {
      const response = await api.get("/aseguradoras");
      setAseguradoras(response.data);
    } catch (error) {
      console.error("Error al obtener aseguradoras:", error);
      notification.error({
        message: "Error",
        description: "No se pudieron cargar las aseguradoras.",
      });
    }
  }, []);

  const fetchZonas = useCallback(async () => {
    try {
      const response = await api.get("/vehiculos/zonas-disponibles");
      setZonas(response.data.zonas || []);
    } catch (error) {
      console.error("Error al obtener zonas:", error);
      notification.error({
        message: "Error",
        description: "No se pudieron cargar las zonas.",
      });
    }
  }, []);

  const fetchDepartamentos = useCallback(async () => {
    try {
      const response = await api.get("/departamentos");
      setDepartamentos(response.data);
    } catch (error) {
      console.error("Error al obtener departamentos:", error);
      notification.error({
        message: "Error",
        description: "No se pudieron cargar los departamentos.",
      });
    }
  }, []);

  const fetchCiudades = useCallback(async (departamentoId, tipo) => {
    try {
      const response = await api.get(
        `/ciudades?departamento_id=${departamentoId}`
      );
      if (tipo === "propietario") {
        setCiudadesPropietario(response.data);
      } else if (tipo === "administrador") {
        setCiudadesAdministrador(response.data);
      }
    } catch (error) {
      console.error("Error al obtener ciudades:", error);
      notification.error({
        message: "Error",
        description: "No se pudieron cargar las ciudades.",
      });
    }
  }, []);

  // Memoizar populateForm para incluirlo en las dependencias
  const populateForm = useCallback(
    (data) => {
      setVehicle({
        ...defaultVehicle,
        ...data,
      });
      // Guardar el estado inicial para detectar cambios
      if (data.estado) {
        setInitialEstado(data.estado);
      }
      // Limpiar observación de cambio de estado
      setEstadoObservacion("");
    },
    []
  );

  // Efecto separado para cargar ciudades cuando se tienen los datos iniciales y departamentos
  useEffect(() => {
    if (isEdit && initialData && departamentos.length > 0) {
      // Cargar ciudades para propietario y administrador si hay datos
      if (initialData.propietario_departamento) {
        // El valor puede venir como ID (string numérico) o como nombre
        const depId = typeof initialData.propietario_departamento === 'string' && !isNaN(initialData.propietario_departamento)
          ? parseInt(initialData.propietario_departamento)
          : null;
        
        if (depId) {
          // Si es un ID numérico, usarlo directamente
          fetchCiudades(depId, "propietario");
        } else {
          // Si es un nombre, buscar por nombre
          const depPropietario = departamentos.find(dep => dep.nombre === initialData.propietario_departamento);
          if (depPropietario) {
            fetchCiudades(depPropietario.id, "propietario");
          }
        }
      }
      if (initialData.administrador_departamento) {
        // El valor puede venir como ID (string numérico) o como nombre
        const depId = typeof initialData.administrador_departamento === 'string' && !isNaN(initialData.administrador_departamento)
          ? parseInt(initialData.administrador_departamento)
          : null;
        
        if (depId) {
          // Si es un ID numérico, usarlo directamente
          fetchCiudades(depId, "administrador");
        } else {
          // Si es un nombre, buscar por nombre
          const depAdministrador = departamentos.find(dep => dep.nombre === initialData.administrador_departamento);
          if (depAdministrador) {
            fetchCiudades(depAdministrador.id, "administrador");
          }
        }
      }
    }
  }, [isEdit, initialData, departamentos, fetchCiudades]);

  // Memoizar resetForm para incluirlo en las dependencias
  const resetForm = useCallback(() => {
    setVehicle(defaultVehicle);
    setErrors({});
    setCiudadesPropietario([]);
    setCiudadesAdministrador([]);
    setInitialEstado(null);
    setEstadoObservacion("");
  }, []);

  useEffect(() => {
    if (open) {
      setLoading(true);
      Promise.all([
        fetchTiposDocumento(),
        fetchAseguradoras(),
        fetchZonas(),
        fetchDepartamentos(),
      ])
        .then(() => {
          if (isEdit && initialData) {
            populateForm(initialData);
          } else {
            resetForm();
          }
        })
        .catch((error) => {
          console.error("Error al cargar datos iniciales:", error);
        })
        .finally(() => setLoading(false));
    }
  }, [
    open,
    fetchTiposDocumento,
    fetchAseguradoras,
    fetchZonas,
    fetchDepartamentos,
    isEdit,
    initialData,
    populateForm,
    resetForm,
  ]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Limpiar el error del campo que se está modificando
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    if (name === "administrador_numero_documento") {
      // Es el usuario de acceso a las apps: solo dígitos (NIT sin dígito de verificación)
      setVehicle((prev) => ({
        ...prev,
        [name]: value.replace(/\D/g, ""),
      }));
    } else if (name === "propietario_telefono" || name === "administrador_telefono") {
      const regex = /^[0-9\b]+$/; // Solo permite números
      if (value === "" || regex.test(value)) {
        if (value.length <= 10) {
          setVehicle((prev) => ({
            ...prev,
            [name]: value,
          }));
        }
      }
    } else {
      setVehicle((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Manejar cambios en Departamento para Propietario
  const handleDepartamentoPropietarioChange = useCallback(
    (value) => {
      setVehicle((prev) => ({
        ...prev,
        propietario_departamento: value,
        propietario_ciudad: "",
      }));
      fetchCiudades(value, "propietario");
    },
    [fetchCiudades]
  );

  // Manejar cambios en Departamento para Administrador
  const handleDepartamentoAdministradorChange = useCallback(
    (value) => {
      setVehicle((prev) => ({
        ...prev,
        administrador_departamento: value,
        administrador_ciudad: "",
      }));
      fetchCiudades(value, "administrador");
    },
    [fetchCiudades]
  );

  // Función para validar el formulario
  const validateForm = () => {
    let valid = true;
    const newErrors = {};

    // Validar campos requeridos
    if (!vehicle.codigo.trim()) {
      newErrors.codigo = "El código es requerido.";
      valid = false;
    }

    if (!vehicle.placa.trim()) {
      newErrors.placa = "La placa es requerida.";
      valid = false;
    }

    if (!vehicle.numero_tarjeta_operacion.trim()) {
      newErrors.numero_tarjeta_operacion = "El número de tarjeta de operación es requerido.";
      valid = false;
    }

    // Validar capacidad de pasajeros
    if (
      vehicle.capacidad_pasajeros !== null &&
      isNaN(vehicle.capacidad_pasajeros)
    ) {
      newErrors.capacidad_pasajeros = "La capacidad debe ser un número.";
      valid = false;
    }

    // Validar kilometraje
    if (vehicle.kilometraje !== null && isNaN(vehicle.kilometraje)) {
      newErrors.kilometraje = "El kilometraje debe ser un número.";
      valid = false;
    }

    // Validar cilindraje
    if (vehicle.cilindraje !== null && isNaN(vehicle.cilindraje)) {
      newErrors.cilindraje = "El cilindraje debe ser un número.";
      valid = false;
    }

    // Validar teléfono propietario
    if (
      vehicle.propietario_telefono &&
      vehicle.propietario_telefono.length !== 10
    ) {
      newErrors.propietario_telefono = "El teléfono debe tener 10 dígitos.";
      valid = false;
    }

    // Validar documento administrador: es su usuario de acceso a las apps
    if (!/^\d+$/.test(String(vehicle.administrador_numero_documento || "").trim())) {
      newErrors.administrador_numero_documento =
        "Solo números, sin puntos ni guion (NIT sin dígito de verificación).";
      valid = false;
    }

    // Validar teléfono administrador
    if (!vehicle.administrador_telefono.trim()) {
      newErrors.administrador_telefono =
        "El teléfono del administrador es requerido.";
      valid = false;
    } else if (vehicle.administrador_telefono.length !== 10) {
      newErrors.administrador_telefono = "El teléfono debe tener 10 dígitos.";
      valid = false;
    }

    // Validar Departamento Propietario
    if (!vehicle.propietario_departamento) {
      newErrors.propietario_departamento = "El departamento es requerido.";
      valid = false;
    }

    // Validar Ciudad Propietario
    if (!vehicle.propietario_ciudad) {
      newErrors.propietario_ciudad = "La ciudad es requerida.";
      valid = false;
    }

    // Validar Departamento Administrador
    if (!vehicle.administrador_departamento) {
      newErrors.administrador_departamento = "El departamento es requerido.";
      valid = false;
    }

    // Validar Ciudad Administrador
    if (!vehicle.administrador_ciudad) {
      newErrors.administrador_ciudad = "La ciudad es requerida.";
      valid = false;
    }

    // Validar direccion administrador (siempre requerida por la base de datos)
    if (!vehicle.administrador_direccion.trim()) {
      newErrors.administrador_direccion = "La dirección es requerida.";
      valid = false;
    }

    // Validar observación cuando cambia el estado (solo en modo edición)
    if (isEdit && initialEstado && vehicle.estado !== initialEstado) {
      if (!estadoObservacion.trim()) {
        newErrors.observacion_cambio_estado = "La observación es requerida cuando cambia el estado.";
        valid = false;
      } else if (estadoObservacion.trim().length < 10) {
        newErrors.observacion_cambio_estado = "La observación debe tener al menos 10 caracteres.";
        valid = false;
      }
    }

    setErrors(newErrors);
    return valid;
  };

  // Función para preparar los datos del vehículo
  const prepareVehicleData = () => {
    console.log("=== DEBUG prepareVehicleData ===");
    console.log("vehicle.propietario_departamento:", vehicle.propietario_departamento);
    console.log("vehicle.propietario_ciudad:", vehicle.propietario_ciudad);
    console.log("vehicle.administrador_departamento:", vehicle.administrador_departamento);
    console.log("vehicle.administrador_ciudad:", vehicle.administrador_ciudad);
    console.log("departamentos array:", departamentos);
    console.log("ciudadesPropietario array:", ciudadesPropietario);
    console.log("ciudadesAdministrador array:", ciudadesAdministrador);
    
    // Buscar departamento por ID o nombre según el tipo
    const propietarioDepartamentoObj = departamentos.find(
      (dep) => {
        // Convertir a número si es string numérico
        const depValue = typeof vehicle.propietario_departamento === 'string' && !isNaN(vehicle.propietario_departamento)
          ? parseInt(vehicle.propietario_departamento)
          : vehicle.propietario_departamento;
        
        return typeof depValue === 'number' 
          ? dep.id === depValue
          : dep.nombre === depValue;
      }
    );
    console.log("propietarioDepartamentoObj found:", propietarioDepartamentoObj);
    
    const propietarioCiudadObj = ciudadesPropietario.find(
      (c) => {
        // Convertir a número si es string numérico
        const cityValue = typeof vehicle.propietario_ciudad === 'string' && !isNaN(vehicle.propietario_ciudad)
          ? parseInt(vehicle.propietario_ciudad)
          : vehicle.propietario_ciudad;
        
        return typeof cityValue === 'number'
          ? c.id === cityValue
          : c.nombre === cityValue;
      }
    );
    console.log("propietarioCiudadObj found:", propietarioCiudadObj);
    
    // Buscar administrador por ID o nombre según el tipo
    const administradorDepartamentoObj = departamentos.find(
      (dep) => {
        // Convertir a número si es string numérico
        const depValue = typeof vehicle.administrador_departamento === 'string' && !isNaN(vehicle.administrador_departamento)
          ? parseInt(vehicle.administrador_departamento)
          : vehicle.administrador_departamento;
        
        return typeof depValue === 'number'
          ? dep.id === depValue
          : dep.nombre === depValue;
      }
    );
    console.log("administradorDepartamentoObj found:", administradorDepartamentoObj);
    
    const administradorCiudadObj = ciudadesAdministrador.find(
      (c) => {
        // Convertir a número si es string numérico
        const cityValue = typeof vehicle.administrador_ciudad === 'string' && !isNaN(vehicle.administrador_ciudad)
          ? parseInt(vehicle.administrador_ciudad)
          : vehicle.administrador_ciudad;
        
        return typeof cityValue === 'number'
          ? c.id === cityValue
          : c.nombre === cityValue;
      }
    );
    console.log("administradorCiudadObj found:", administradorCiudadObj);

    // Función auxiliar para formatear fechas correctamente
    const formatDateForBackend = (dateValue) => {
      if (!dateValue) return null;
      // Si es un string en formato YYYY-MM-DD, usarlo directamente
      if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        return dateValue;
      }
      // Si es una fecha ISO o dayjs, extraer solo la fecha sin timezone
      return dayjs(dateValue).format('YYYY-MM-DD');
    };

    const finalData = {
      ...vehicle,
      usuario_id: user.id,
      // Fechas de convenio
      fecha_vinculacion: formatDateForBackend(vehicle.fecha_vinculacion),
      fecha_matricula: formatDateForBackend(vehicle.fecha_matricula),
      fecha_vencimiento_convenio: formatDateForBackend(vehicle.fecha_vencimiento_convenio),
      flota_propia: typeof vehicle.flota_propia === "boolean" ? vehicle.flota_propia : null,
      // Todas las fechas de vencimiento
      fecha_vencimiento_preventiva: formatDateForBackend(vehicle.fecha_vencimiento_preventiva),
      fecha_vencimiento_soat: formatDateForBackend(vehicle.fecha_vencimiento_soat),
      fecha_vencimiento_tecnomecanica: formatDateForBackend(vehicle.fecha_vencimiento_tecnomecanica),
      fecha_vencimiento_contractual: formatDateForBackend(vehicle.fecha_vencimiento_contractual),
      fecha_vencimiento_extracontractual: formatDateForBackend(vehicle.fecha_vencimiento_extracontractual),
      fecha_vencimiento_tarjeta_operacion: formatDateForBackend(vehicle.fecha_vencimiento_tarjeta_operacion),
      fecha_vencimiento_todo_riesgo: formatDateForBackend(vehicle.fecha_vencimiento_todo_riesgo),
      capacidad_pasajeros:
        vehicle.capacidad_pasajeros !== null
          ? vehicle.capacidad_pasajeros
          : null,
      cilindraje: vehicle.cilindraje !== null ? vehicle.cilindraje : null,
      kilometraje: vehicle.kilometraje !== null ? vehicle.kilometraje : null,
      // Enviar ID para estos campos (la base de datos espera IDs):
      propietario_departamento: propietarioDepartamentoObj
        ? propietarioDepartamentoObj.id
        : "",
      propietario_ciudad: propietarioCiudadObj
        ? propietarioCiudadObj.id
        : "",
      administrador_departamento: administradorDepartamentoObj
        ? administradorDepartamentoObj.id
        : "",
      administrador_ciudad: administradorCiudadObj
        ? administradorCiudadObj.id
        : "",
    };

    // Agregar datos de cambio de estado si aplica (solo en modo edición)
    if (isEdit && initialEstado && vehicle.estado !== initialEstado) {
      finalData.estado_anterior = initialEstado;
      finalData.observacion_cambio_estado = estadoObservacion.trim();
    }

    console.log("=== FINAL DATA TO SEND ===");
    console.log("propietario_departamento ID:", propietarioDepartamentoObj ? propietarioDepartamentoObj.id : "");
    console.log("propietario_ciudad ID:", propietarioCiudadObj ? propietarioCiudadObj.id : "");
    console.log("administrador_departamento ID:", administradorDepartamentoObj ? administradorDepartamentoObj.id : "");
    console.log("administrador_ciudad ID:", administradorCiudadObj ? administradorCiudadObj.id : "");
    console.log("===============================");
    
    return finalData;
  };

  // Función para agregar un nuevo vehículo
  const sendAddVehicle = async (vehicleData) => {
    return api.post("/vehiculos", vehicleData);
  };

  // Función para editar un vehículo existente
  const sendEditVehicle = async (vehicleData) => {
    if (!initialData?.id) {
      throw new Error("Datos iniciales incompletos para la edición.");
    }
    return api.put(`/vehiculos/${initialData.id}`, vehicleData);
  };

  // Función auxiliar para enviar datos según el modo (creación o edición)
  const sendVehicleData = async (vehicleData) => {
    return isEdit ? sendEditVehicle(vehicleData) : sendAddVehicle(vehicleData);
  };

  // Función auxiliar para mostrar notificación de éxito
  const showSuccessNotification = (cuenta) => {
    notification.success({
      message: isEdit ? "Vehículo actualizado" : "Vehículo agregado",
      description: isEdit
        ? "El vehículo se ha actualizado correctamente."
        : "El vehículo se ha agregado correctamente.",
    });
    const avisosCuenta = {
      creada: `Se creó el ingreso a plataforma del administrador. Usuario: ${cuenta?.usuario}, contraseña inicial: mogotax123 (deberá cambiarla al ingresar).`,
      relacionada: `El vehículo se relacionó con la cuenta existente del administrador (${cuenta?.vehiculos} vehículo(s)).`,
    };
    if (cuenta && avisosCuenta[cuenta.accion]) {
      notification.info({
        message: "Ingreso a plataforma del administrador",
        description: cuenta.es_conductor && cuenta.accion === "creada"
          ? `Se habilitó el perfil de administrador. Como también es conductor, ingresa con su documento y su contraseña de conductor.`
          : avisosCuenta[cuenta.accion],
        duration: 10,
      });
    }
  };

  // Función auxiliar para mostrar notificación de error
  const showErrorNotification = () => {
    notification.error({
      message: isEdit
        ? "Error al editar vehículo"
        : "Error al agregar vehículo",
      description:
        "Ha ocurrido un error al guardar los datos. Por favor, intenta nuevamente.",
    });
  };

  // Función auxiliar para manejar errores
  const handleError = (error) => {
    console.error(
      isEdit ? "Error al editar vehículo:" : "Error al agregar vehículo:",
      error
    );

    if (
      error.response &&
      [400, 422].includes(error.response.status) &&
      error.response.data.errors
    ) {
      const fieldErrors = Object.fromEntries(
        Object.entries(error.response.data.errors).map(([campo, msg]) => [
          campo,
          Array.isArray(msg) ? msg[0] : msg,
        ])
      );
      setErrors(fieldErrors);
      if (fieldErrors.administrador_numero_documento) {
        notification.error({
          message: "Documento del administrador",
          description: fieldErrors.administrador_numero_documento,
        });
      }

      // Definir los campos que tienen restricciones únicas
      const uniqueFields = ["placa"];

      uniqueFields.forEach((field) => {
        if (fieldErrors[field]) {
          let fieldName = "";
          if (field === "placa") {
            fieldName = "Placa";
          }
          // Agrega más condiciones if-else si tienes otros campos únicos
          else {
            fieldName = field;
          }
          notification.error({
            message: `Error en ${fieldName}`,
            description: fieldErrors[field],
          });
        }
      });
    } else {
      showErrorNotification();
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const vehicleData = prepareVehicleData();
      const response = await sendVehicleData(vehicleData);

      // Refrescar la lista de vehículos y cerrar el modal
      fetchVehicles();
      handleClose();

      // Mostrar notificación de éxito
      showSuccessNotification(response?.data?.administrador_cuenta);

      // Resetear el formulario después de enviar si no es edición
      if (!isEdit) {
        resetForm();
      }
    } catch (error) {
      handleError(error);
    }
  };

  const copiarDatosPropietario = () => {
    setVehicle((prev) => ({
      ...prev,
      administrador_nombre: prev.propietario_nombre,
      administrador_numero_documento: prev.propietario_numero_documento,
      administrador_tipo_documento_id: prev.propietario_tipo_documento_id,
      administrador_telefono: prev.propietario_telefono,
      administrador_correo: prev.propietario_correo,
      administrador_direccion: prev.propietario_direccion,
      administrador_barrio: prev.propietario_barrio,
      administrador_localidad: prev.propietario_localidad,
      administrador_departamento: prev.propietario_departamento,
      administrador_ciudad: prev.propietario_ciudad,
    }));

    // Cargar ciudades del administrador
    if (vehicle.propietario_departamento) {
      fetchCiudades(vehicle.propietario_departamento, "administrador");
    }
  };

  const limpiarDatosAdministrador = () => {
    setVehicle((prev) => ({
      ...prev,
      administrador_nombre: "",
      administrador_numero_documento: "",
      administrador_tipo_documento_id: "",
      administrador_telefono: "",
      administrador_correo: "",
      administrador_direccion: "",
      administrador_barrio: "",
      administrador_localidad: "",
      administrador_departamento: "",
      administrador_ciudad: "",
    }));
    setCiudadesAdministrador([]);
  };

  const handleSwitchChange = (checked) => {
    setMismoAdministrador(checked);
    if (checked) {
      copiarDatosPropietario();
    } else {
      limpiarDatosAdministrador();
    }
  };

  // Cuenta existente con el documento del administrador (solo si se activa el ingreso a plataforma)
  const documentoAdministrador = vehicle.administrador_numero_documento;
  const accesoAdministrador = vehicle.administrador_acceso_app === true;
  useEffect(() => {
    if (!open || !accesoAdministrador || !/^\d{5,}$/.test(documentoAdministrador || "")) {
      setCuentaAdministrador(null);
      return undefined;
    }
    let vigente = true;
    const espera = setTimeout(() => {
      api
        .get("/vehiculos/administradores/buscar", { params: { documento: documentoAdministrador } })
        .then((res) => vigente && setCuentaAdministrador(res.data))
        .catch(() => vigente && setCuentaAdministrador(null));
    }, 400);
    return () => {
      vigente = false;
      clearTimeout(espera);
    };
  }, [open, accesoAdministrador, documentoAdministrador]);

  const otrosVehiculosCuenta = (cuentaAdministrador?.vehiculos || []).filter(
    (v) => v.id !== initialData?.id
  );

  const adminHeaderSwitch = (
    <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ color: "white" }}>Usar datos del propietario</span>
        <Switch checked={mismoAdministrador} onChange={handleSwitchChange} />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ color: "white" }}>Generar ingreso a plataforma</span>
        <Switch
          checked={accesoAdministrador}
          checkedChildren="Sí"
          unCheckedChildren="No"
          onChange={(checked) =>
            setVehicle((prev) => ({ ...prev, administrador_acceso_app: checked }))
          }
        />
      </div>
    </div>
  );

  const avisoIngresoAdministrador = accesoAdministrador && (
    <Alert
      style={{ marginBottom: 16 }}
      showIcon
      type={cuentaAdministrador?.existe ? "info" : "success"}
      message={
        cuentaAdministrador?.existe
          ? `Ya existe una cuenta de administrador con el documento ${cuentaAdministrador.documento}`
          : "Al guardar se creará el ingreso a plataforma del administrador"
      }
      description={
        <>
          {cuentaAdministrador?.existe ? (
            <div>
              El vehículo se relacionará con esa cuenta
              {otrosVehiculosCuenta.length > 0 &&
                ` (ya tiene: ${otrosVehiculosCuenta.map((v) => v.codigo || v.placa).join(", ")})`}
              . Nombre, correo y teléfono de la cuenta se actualizarán con los de este formulario.
            </div>
          ) : (
            <div>
              Usuario: número de documento del administrador. Contraseña inicial: mogotax123 (se pide cambiarla al ingresar).
            </div>
          )}
          {cuentaAdministrador?.es_conductor && (
            <div>
              Este documento también es de un conductor: usa una sola contraseña (la de conductor) y elige el perfil al ingresar.
            </div>
          )}
          <div>Acceso de solo lectura a los traslados de sus vehículos en la app de conductores.</div>
        </>
      }
    />
  );

  return (
    <Modal
      title={isEdit ? "Editar Vehículo" : "Agregar Vehículo"}
      open={open}
      onCancel={() => {
        handleClose();
        resetForm();
      }}
      footer={[
        <Button
          key="cancel"
          onClick={() => {
            handleClose();
            resetForm();
          }}
        >
          Cancelar
        </Button>,
        <Button key="submit" type="primary" onClick={handleSubmit}>
          {isEdit ? "Guardar Cambios" : "Agregar"}
        </Button>,
      ]}
      width={900}
      style={{ maxHeight: "80vh", overflowY: "auto" }}
      destroyOnHidden={true}
    >
      <Spin spinning={loading} tip="Cargando información...">
        <Form layout="vertical">
          {/* Información del Vehículo */}
          <VehicleInfo
            vehicle={vehicle}
            handleInputChange={handleInputChange}
            zonas={zonas}
            errors={errors}
            isEdit={isEdit}
            initialEstado={initialEstado}
            estadoObservacion={estadoObservacion}
            onEstadoObservacionChange={setEstadoObservacion}
          />

          {/* Generación de documentos (FUEC y preoperacional) */}
          <GeneracionDocumentos
            values={vehicle}
            handleInputChange={handleInputChange}
            entidad="vehículo"
          />
          <SiNoSinDefinir
            name="flota_propia"
            label="Flota propia"
            value={vehicle.flota_propia}
            handleInputChange={handleInputChange}
            ayuda="Indica si el vehículo es propio de MOGOTAX."
          />

          {/* Datos del Propietario */}
          <OwnerInfo
            vehicle={vehicle}
            handleInputChange={handleInputChange}
            tiposDocumento={tiposDocumento}
            departamentos={departamentos}
            ciudades={ciudadesPropietario}
            handleDepartamentoChange={handleDepartamentoPropietarioChange}
            errors={errors}
          />

          {/* Datos del Administrador */}
          <AdminInfo
            vehicle={vehicle}
            handleInputChange={handleInputChange}
            tiposDocumento={tiposDocumento}
            departamentos={departamentos}
            ciudades={ciudadesAdministrador}
            handleDepartamentoChange={handleDepartamentoAdministradorChange}
            errors={errors}
            extraHeader={adminHeaderSwitch}
            disableDireccionFields={mismoAdministrador}
          />
          {avisoIngresoAdministrador}

          {/* Convenio */}
          <ContractInfo
            vehicle={vehicle}
            handleInputChange={handleInputChange}
            errors={errors}
          />

          {/* Fechas de Vencimiento */}
          <ExpirationDatesInfo
            vehicle={vehicle}
            handleInputChange={handleInputChange}
            aseguradoras={aseguradoras}
            errors={errors}
          />
        </Form>
      </Spin>
    </Modal>
  );
};

AddVehicleForm.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  fetchVehicles: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  isEdit: PropTypes.bool,
};

export default AddVehicleForm;
